import axios, { AxiosResponse } from "axios";
import { IFailCallbackParam, AnyType } from "../constants";
import { getErrorStatus } from "./getErrorStatus";
import AuthService from "../../common/AuthService";

type DownloadData = AnyType;

export interface ICallbackParams extends IFailCallbackParam {
  /**
   * WebAPIでダウンロードが成功した場合に呼び出されます
   * @callback success
   * @param {number} status - httpステータス
   */
  success?: (status: number) => void;
}

function AxiosGet(url: string, token?: string) {
  if (token) {
    return axios.get(url, {
      headers: {
        Authorization: "Bearer " + token,
      },
      responseType: "arraybuffer",
    });
  } else {
    return axios.get(url, { responseType: "arraybuffer" });
  }
}

interface IAxiosInternalParams extends ICallbackParams {
  filename: string;
  promise: Promise<AxiosResponse>;
}

function getFileName(disposition: string | undefined, notExistFname: string) {
  if (disposition) {
    const filenames = disposition.match(/filename=(.*);/);
    const utf8Filenames = disposition.match(/filename\*=UTF-8''(.*)/);
    if (utf8Filenames && utf8Filenames.length > 1) {
      return decodeURI(utf8Filenames[1]);
    } else if (filenames && filenames.length > 1) {
      return filenames[1];
    } else {
      return notExistFname;
    }
  } else {
    return notExistFname;
  }
}

function createFile(fname: string, data: DownloadData) {
  const blob = new Blob([data], { type: "application/octet-stream" });
  const fileDownloadUrl = URL.createObjectURL(blob);
  const link = document.createElement("a");
  document.body.appendChild(link);
  link.href = fileDownloadUrl;
  link.setAttribute("download", fname);
  link.click();
  document.body.removeChild(link);
}

function AxiosInternal(params: IAxiosInternalParams) {
  params.promise
    .then((res) => {
      const { data, status, headers } = res;
      const fname = getFileName(
        headers["content-disposition"],
        params.filename
      );
      createFile(fname, data);
      if (params.success) {
        params.success(status);
      }
    })
    .catch((err) => {
      const { id, status } = getErrorStatus(err);
      params.fail(id, status);
    });
}

/**
 * WebAPIからダウンロードします
 * @function download
 * @param {string} url - データダウンロードURL
 * @param {string} filename - 作成ファイル名
 * @param {ICallbackParams} callback - コールバックパラメータ
 * @param {AuthService} authService - 認証オブジェクト
 */
export default function download(
  url: string,
  filename: string,
  callback: ICallbackParams,
  authService?: AuthService
): void {
  if (authService) {
    authService.getToken({
      success: (token) => {
        AxiosInternal({
          filename: filename,
          promise: AxiosGet(url, token),
          success: callback.success,
          fail: (id, status) => {
            if (status === 401) {
              downloadRetry401(authService, url, filename, callback);
            } else {
              callback.fail(id, status);
            }
          },
        });
      },
      fail: (err) => {
        const { id, status } = getErrorStatus(err);
        callback.fail(id, status);
      },
    });
  } else {
    AxiosInternal({
      filename: filename,
      promise: AxiosGet(url),
      success: callback.success,
      fail: callback.fail,
    });
  }
}

/**
 * WebAPIからダウンロードします
 * @function downloadRetry401
 * @param {AuthService} authService - 認証オブジェクト
 * @param {string} url - データダウンロードURL
 * @param {string} filename - 作成ファイル名
 * @param {ICallbackParams} callback - コールバックパラメータ
 */
function downloadRetry401(
  authService: AuthService,
  url: string,
  filename: string,
  callback: ICallbackParams
): void {
  authService.updateToken({
    success: (token) => {
      AxiosInternal({
        filename: filename,
        promise: AxiosGet(url, token),
        success: callback.success,
        fail: callback.fail,
      });
    },
    fail: (err) => {
      const { id, status } = getErrorStatus(err);
      callback.fail(id, status);
    },
  });
}
